import React, { useEffect, useRef } from "react";
import { Helmet } from 'react-helmet-async';
import { motion, useInView, useAnimation } from "framer-motion";
import MarketingLayout from "./MarketingLayout";
import { generateBreadcrumbJsonLd } from "./utils/breadcrumbJsonLd";
import {
  CheckCircle2,
  ArrowRight,
  CreditCard,
  Shield,
  Zap,
  DollarSign,
  Sparkles
} from "lucide-react";
import { Link } from "react-router-dom";

// アニメーション用のカスタムフック
const useScrollAnimation = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [isInView, controls]);

  return { ref, controls, isInView };
};

const PricingPage: React.FC = () => {
  const pricingFeatures = [
    "初期費用0円",
    "月額費用0円",
    "カード決済対応",
    "ドメイン・サーバー費用込み",
    "主要機能を利用可能"
  ];

  const flowSteps = [
    {
      step: "1",
      title: "ポイント購入",
      description: "お客様がクレジットカードでポイントを購入",
      icon: <CreditCard className="w-8 h-8" />,
      color: "bg-yellow-400"
    },
    {
      step: "2",
      title: "売上確定",
      description: "ポイント購入時に売上が確定",
      icon: <ArrowRight className="w-8 h-8" />,
      color: "bg-blue-400"
    },
    {
      step: "3",
      title: "入金実行",
      description: "手数料を差し引いた額が入金",
      icon: <DollarSign className="w-8 h-8" />,
      color: "bg-green-400"
    }
  ];

  // アニメーションバリアント
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  const pricingAnimation = useScrollAnimation();
  const flowAnimation = useScrollAnimation();
  const faqAnimation = useScrollAnimation();

  return (
    <MarketingLayout>
      <Helmet>
        <title>料金プラン | JOYBASE</title>
        <meta name="description" content="JOYBASEの料金プラン。初期費用・月額費用0円。カード・ウォレット決済手数料は3.6%、PayPayは9.0%（準備中）で、別途システム利用料3%がかかります。" />
        <meta property="og:title" content="料金プラン | JOYBASE" />
        <meta property="og:description" content="初期費用・月額費用0円。カード・ウォレット決済手数料は3.6%、PayPayは9.0%（準備中）で、別途システム利用料3%がかかります。" />
        <meta property="og:url" content="https://www.joybase.jp/pricing" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="JOYBASE" />
        <meta property="og:image" content="https://www.joybase.jp/ogp.svg" />
        <meta property="og:locale" content="ja_JP" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="料金プラン | JOYBASE" />
        <meta name="twitter:description" content="初期費用・月額費用0円。決済手数料はカード・ウォレット3.6%、PayPay 9.0%（準備中）です。" />
        <meta name="twitter:image" content="https://www.joybase.jp/ogp.svg" />
        <script type="application/ld+json">
        {generateBreadcrumbJsonLd([
          { name: "ホーム", url: "https://www.joybase.jp/" },
          { name: "料金", url: "https://www.joybase.jp/pricing" }
        ])}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#FFFEF5]">
        {/* ヘッダーセクション */}
        <section className="py-20 pt-32 relative overflow-hidden">
          {/* デコレーション */}
          <motion.div
            animate={{ y: [0, -10, 0], rotate: [0, 5, 0] }}
            transition={{ duration: 5, repeat: Infinity }}
            className="absolute top-32 left-[10%] w-12 h-12 bg-yellow-400 border-4 border-black rounded-lg shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hidden lg:block"
          />
          <motion.div
            animate={{ y: [0, 10, 0], rotate: [0, -5, 0] }}
            transition={{ duration: 6, repeat: Infinity }}
            className="absolute top-40 right-[15%] w-10 h-10 bg-green-400 border-4 border-black rounded-full shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hidden lg:block"
          />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-yellow-400 border-4 border-black text-black rounded-full text-sm font-bold mb-8 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                <Zap className="w-5 h-5" />
                初期費用0円で始められる
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6">
                シンプルな<span className="relative inline-block">
                  <span className="relative z-10">料金体系</span>
                  <span className="absolute bottom-2 left-0 w-full h-4 bg-yellow-400 -z-0"></span>
                </span>
              </h1>
              <p className="text-xl text-gray-700 max-w-3xl mx-auto font-medium">
                初期費用0円・月額費用0円。決済手数料はカード・ウォレット3.6%、PayPay 9.0%（準備中）。<br />
                別途システム利用料3%がかかります（表示料率はすべて税抜）。
              </p>
            </motion.div>
          </div>
        </section>

        {/* 料金プラン */}
        <motion.section
          ref={pricingAnimation.ref}
          className="py-16"
          initial="hidden"
          animate={pricingAnimation.controls}
          variants={containerVariants}
        >
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              className="bg-white border-4 border-black rounded-3xl overflow-hidden shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]"
              variants={itemVariants}
            >
              <div className="p-8 bg-yellow-400 border-b-4 border-black">
                <h2 className="text-3xl font-black text-black">スタンダードプラン</h2>
                <p className="text-gray-900 mt-2 font-medium">すべての機能が使えるプラン</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 divide-y-4 md:divide-y-0 md:divide-x-4 divide-black">
                <div className="p-8">
                  <h3 className="text-xl font-black mb-6 text-black flex items-center gap-2">
                    <span className="w-2 h-6 bg-green-400 border-2 border-black"></span>
                    オリパサイトを始めるとき
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-4 bg-green-100 border-2 border-black rounded-xl">
                      <span className="font-bold text-gray-900">初期費用</span>
                      <span className="text-4xl font-black text-black">0円</span>
                    </div>
                    <div className="flex justify-between items-center p-4 bg-green-100 border-2 border-black rounded-xl">
                      <span className="font-bold text-gray-900">月額費用</span>
                      <span className="text-4xl font-black text-black">0円</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-4 font-medium">
                      ※アカウント作成・サイト開設に一切費用はかかりません
                    </p>
                  </div>
                </div>

                <div className="p-8">
                  <h3 className="text-xl font-black mb-6 text-black flex items-center gap-2">
                    <span className="w-2 h-6 bg-yellow-400 border-2 border-black"></span>
                    オリパが購入されたとき
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-4 bg-yellow-100 border-2 border-black rounded-xl">
                      <span className="font-bold text-gray-900">カード・ウォレット決済手数料</span>
                      <span className="text-4xl font-black text-yellow-600">3.6%</span>
                    </div>
                    <div className="flex justify-between items-center p-4 bg-yellow-100 border-2 border-black rounded-xl">
                      <span className="font-bold text-gray-900">システム利用料</span>
                      <span className="text-4xl font-black text-yellow-600">3%</span>
                    </div>
                    <p className="text-sm text-gray-600 font-medium">
                      ※ 表示の手数料率はすべて税抜です。別途消費税がかかります。
                    </p>
                    <p className="text-sm text-gray-600 font-medium">
                      ※ PayPayの場合は決済手数料9.0%（準備中）です。
                    </p>
                  </div>
                </div>
              </div>

              <div className="p-6 bg-gray-100 border-t-4 border-black">
                <h4 className="font-black text-gray-900 mb-4">プランに含まれる内容</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {pricingFeatures.map((feature, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-green-400 border-2 border-black rounded flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="w-4 h-4 text-black" />
                      </div>
                      <span className="text-gray-800 font-medium">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-6 bg-gray-100 border-t-4 border-black space-y-3">
                <p className="text-base text-gray-800 font-bold flex items-center gap-2">
                  <div className="w-6 h-6 bg-green-400 border-2 border-black rounded flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-4 h-4 text-black" />
                  </div>
                  実際にユーザーがオリパを購入するまで料金は発生しません
                </p>
                <p className="text-base text-gray-800 font-bold flex items-center gap-2">
                  <div className="w-6 h-6 bg-green-400 border-2 border-black rounded flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-4 h-4 text-black" />
                  </div>
                  ドメイン・サーバー費用はシステム利用料に含まれています
                </p>
              </div>

              {/* 追加の強調ポイント */}
              <div className="p-6 bg-yellow-100 border-t-4 border-black">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-yellow-400 border-2 border-black rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Sparkles className="w-5 h-5 text-black" />
                  </div>
                  <div>
                    <p className="font-black text-black mb-1">サーバー費用の心配なし</p>
                    <p className="text-sm text-gray-700">
                      他社では別途発生するドメイン・サーバー費用がJOYBASEでは不要。<br />
                      イベント時の急激なアクセス増加もシステム利用料の中で対応いたします。
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* 決済システムの仕組み */}
        <section className="py-16 bg-white border-t-4 border-b-4 border-black">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-black mb-4">安全な決済システム</h2>
              <p className="text-gray-700 text-lg font-medium">
                信頼性の高い決済プラットフォームを採用
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <motion.div
                className="bg-blue-100 border-4 border-black rounded-2xl p-8 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]"
                whileHover={{ translateX: 2, translateY: 2, boxShadow: "2px 2px 0px 0px rgba(0,0,0,1)" }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-14 h-14 bg-white border-4 border-black rounded-xl flex items-center justify-center shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]">
                    <Shield className="w-7 h-7 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-black">自動入金システム</h3>
                </div>
                <p className="text-gray-800 leading-relaxed font-medium">
                  マーケットプレイス向けの決済システムを採用しています。
                  JOYBASEを通じて行われた取引の代金を、自動的にあなたの口座に振り込みます。
                </p>
              </motion.div>

              <motion.div
                className="bg-green-100 border-4 border-black rounded-2xl p-8 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]"
                whileHover={{ translateX: 2, translateY: 2, boxShadow: "2px 2px 0px 0px rgba(0,0,0,1)" }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-14 h-14 bg-white border-4 border-black rounded-xl flex items-center justify-center shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]">
                    <CheckCircle2 className="w-7 h-7 text-green-600" />
                  </div>
                  <h3 className="text-xl font-black">セキュリティと信頼性</h3>
                </div>
                <p className="text-gray-800 leading-relaxed font-medium">
                  PCI-DSS Level 1準拠の決済基盤を採用し、カード情報を保護します。
                  不正利用対策や3Dセキュアにも対応しています。
                </p>
              </motion.div>
            </div>

            <motion.div
              className="mt-6 bg-purple-100 border-4 border-black rounded-2xl p-8 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]"
              whileHover={{ translateX: 2, translateY: 2, boxShadow: "2px 2px 0px 0px rgba(0,0,0,1)" }}
            >
              <h3 className="text-xl font-black mb-4">決済の準備</h3>
              <div className="space-y-4 text-gray-800">
                <p className="font-medium">JOYBASEで決済を受け付けるには、決済サービスでの審査が必要です：</p>
                <ol className="list-none space-y-2">
                  {[
                    "JOYBASEでサイトをセットアップ",
                    "商品・オリパを登録",
                    "セットアップ完了後、決済サービスの審査",
                    "審査完了後、販売開始"
                  ].map((step, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <span className="w-8 h-8 bg-purple-400 border-2 border-black rounded-full flex items-center justify-center text-sm font-black">
                        {index + 1}
                      </span>
                      <span className="font-medium">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </motion.div>
          </div>
        </section>

        {/* 入金フロー */}
        <motion.section
          ref={flowAnimation.ref}
          className="py-16 bg-[#FFFEF5]"
          initial="hidden"
          animate={flowAnimation.controls}
          variants={containerVariants}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div className="text-center mb-12" variants={itemVariants}>
              <h2 className="text-3xl lg:text-4xl font-black mb-4">入金までの流れ</h2>
              <p className="text-gray-700 text-lg font-medium">
                購入から入金まで、すべて自動で処理されます
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {flowSteps.map((item, index) => (
                <motion.div
                  key={index}
                  className="relative"
                  variants={itemVariants}
                >
                  <div className={`${item.color} border-4 border-black rounded-2xl p-6 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-1 hover:translate-y-1 transition-all text-center h-full`}>
                    <div className="w-16 h-16 bg-white border-4 border-black rounded-xl flex items-center justify-center mx-auto mb-4 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)]">
                      {item.icon}
                    </div>
                    <div className="text-4xl font-black text-black/20 mb-2">{item.step}</div>
                    <h3 className="text-lg font-black mb-2">{item.title}</h3>
                    <p className="text-gray-800 text-sm font-medium">{item.description}</p>
                  </div>
                  {index < flowSteps.length - 1 && (
                    <div className="hidden md:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                      <ArrowRight className="w-6 h-6 text-black" />
                    </div>
                  )}
                </motion.div>
              ))}
            </div>

          </div>
        </motion.section>

        {/* FAQ */}
        <motion.section
          ref={faqAnimation.ref}
          className="py-16 bg-white border-t-4 border-black"
          initial="hidden"
          animate={faqAnimation.controls}
          variants={containerVariants}
        >
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div className="text-center mb-12" variants={itemVariants}>
              <h2 className="text-3xl lg:text-4xl font-black mb-4">よくある質問</h2>
            </motion.div>

            <motion.div className="space-y-4" variants={containerVariants}>
              {[
                {
                  q: "最低利用期間や解約金はありますか？",
                  a: "最低利用期間はありません。いつでも自由に始めて、いつでも停止できます。解約金も一切かかりません。",
                  color: "bg-yellow-100"
                },
                {
                  q: "手数料以外に追加費用は発生しますか？",
                  a: "カード・ウォレット決済手数料は3.6%、PayPay決済手数料は9.0%（準備中）です。いずれも別途システム利用料3%と消費税がかかります。ドメイン・サーバー費用はシステム利用料に含まれています。",
                  color: "bg-blue-100"
                },
                {
                  q: "売上が少なくても利用できますか？",
                  a: "はい、売上の大小に関わらずご利用いただけます。初期費用・月額費用が0円なので、少額の売上から始めても問題ありません。",
                  color: "bg-green-100"
                }
              ].map((faq, index) => (
                <motion.div
                  key={index}
                  className={`${faq.color} border-4 border-black rounded-2xl p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]`}
                  variants={itemVariants}
                  whileHover={{ translateX: 2, translateY: 2, boxShadow: "0px 0px 0px 0px rgba(0,0,0,1)" }}
                >
                  <h3 className="font-black text-lg mb-2 text-black">{faq.q}</h3>
                  <p className="text-gray-800 font-medium">{faq.a}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </motion.section>

        {/* CTA */}
        <section className="py-20 bg-black relative overflow-hidden">
          {/* デコレーション */}
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="absolute top-10 left-10 w-24 h-24 border-4 border-yellow-400/30 rounded-xl"
          />
          <motion.div
            animate={{ rotate: -360 }}
            transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
            className="absolute bottom-10 right-10 w-32 h-32 border-4 border-pink-400/30 rounded-full"
          />

          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-6">
                初期費用<span className="text-yellow-400">0円</span>でサイト開設を申し込もう
              </h2>
              <p className="text-xl text-gray-300 mb-10 font-medium">
                初期費用・月額費用0円。販売開始には決済加盟店審査が必要です
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.a
                  href="https://dashboard.joybase.jp/signup"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-10 py-5 bg-yellow-400 text-black border-4 border-yellow-400 rounded-xl font-black text-lg shadow-[6px_6px_0px_0px_rgba(255,255,255,0.3)] hover:shadow-[2px_2px_0px_0px_rgba(255,255,255,0.3)] hover:translate-x-1 hover:translate-y-1 transition-all"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  無料でサイト開設を申し込む
                  <ArrowRight className="w-5 h-5" />
                </motion.a>
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Link
                    to="/features"
                    className="inline-flex items-center justify-center gap-2 px-10 py-5 bg-white text-black border-4 border-white rounded-xl font-black text-lg shadow-[6px_6px_0px_0px_rgba(255,255,255,0.3)] hover:shadow-[2px_2px_0px_0px_rgba(255,255,255,0.3)] hover:translate-x-1 hover:translate-y-1 transition-all"
                  >
                    機能を詳しく見る
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
};

export default PricingPage;
